import { isEmpty, isNil } from 'lodash';

/**
 * A refused listing, arranged so a view can ask it about one filter.
 *
 * The sibling of {@link ValidationErrorIndex}, for the other half of the contract. That
 * one reads a **body** check: 200, an array of objects, paths spelled `$.auto.majorMultiplier`.
 * This one reads a **query** refusal: 400, an array of strings, names spelled `?firstAssertedAt`.
 * A `GET` has no body to hold a `$.path`, so a listing that refuses a filter can only name
 * the query parameter the caller chose.
 *
 * <h2>What this exists to get right</h2>
 *
 * <ul>
 *  <li><strong>Reading `errors`, not only `detail`.</strong> `detail` carries the single
 *      message when exactly one thing was refused and the literal
 *      {@link MULTIPLE_ERRORS} when several were. A filter set can refuse two parameters
 *      at once, so a view reading `detail` alone shows the reader a placeholder on
 *      exactly the request that needed an explanation.</li>
 *
 *  <li><strong>Splitting on the first `: ` and no further.</strong> The message is prose
 *      and contains colons of its own — an ISO-8601 instant has two. Everything after the
 *      first separator is the message, whole.</li>
 *
 *  <li><strong>Never losing one.</strong> An entry that does not name a parameter is
 *      still a reason the listing was refused. {@link unclaimed} is what a view puts in
 *      its own block, so a rule the service ships after this console was written is a
 *      visible refusal rather than a blank message over an empty table.</li>
 *
 *  <li><strong>Keeping the prefix when the line stands alone.</strong> {@link lines}
 *      returns each entry as the service wrote it, because in a growler the parameter
 *      name is the half that says what to change. {@link messageFor} drops it, because
 *      there the control the message sits beside has already said which filter it is.</li>
 * </ul>
 *
 * Immutable, and cheap to ask: the work happens once, in the constructor.
 */
export class QueryErrorIndex {
    /** Every entry as the service wrote it, in the order it listed them. */
    private readonly entries: string[] = [];

    /** The message half, by parameter name, first one wins. */
    private readonly byParameter = new Map<string, string>();

    /** The entries that named no parameter. */
    private readonly unnamed: string[] = [];

    constructor(entries: string[] = []) {
        for (const entry of entries || []) {
            const line = (entry || '').trim();
            if (isEmpty(line)) {
                continue;
            }
            this.entries.push(line);
            const named = parse(line);
            if (isNil(named)) {
                this.unnamed.push(line);
                continue;
            }
            if (!this.byParameter.has(named.parameter)) {
                this.byParameter.set(named.parameter, named.message);
            }
        }
    }

    /**
     * The refusal in a failed response, or an empty index.
     *
     * Takes the error as the caller caught it — an `HttpErrorResponse`, whose parsed body
     * is under `error`. Anything that is not a listing refusal gives an empty index rather
     * than throwing, so a caller can ask first and fall back to its generic message.
     *
     * `errors` wins when it is there. `detail` is the fallback, and is skipped when it
     * holds {@link MULTIPLE_ERRORS} — that string is a stand-in for the array, and showing
     * it says only that something went wrong twice.
     */
    static from(error: any): QueryErrorIndex {
        const body = error?.error;
        const errors = body?.errors;
        if (Array.isArray(errors) && !isEmpty(errors)) {
            return new QueryErrorIndex(errors.filter((entry: any) => typeof entry === 'string'));
        }
        const detail = body?.detail;
        if (typeof detail !== 'string' || isEmpty(detail.trim()) || detail.trim() === MULTIPLE_ERRORS) {
            return new QueryErrorIndex();
        }
        return new QueryErrorIndex([detail]);
    }

    /** True when there was nothing to read — not a refusal, or one with no entries. */
    get isEmpty(): boolean {
        return this.entries.length === 0;
    }

    /** The parameters named, in the order the service listed them. */
    get parameters(): string[] {
        return [...this.byParameter.keys()];
    }

    /** Every entry whole, prefix and all — what to show when a line stands on its own. */
    get lines(): string[] {
        return [...this.entries];
    }

    /** The entries that named no parameter, so a view can show them rather than drop them. */
    get unclaimed(): string[] {
        return [...this.unnamed];
    }

    /**
     * What to show beside one filter control, or `''`.
     *
     * The message without the `?name: ` prefix: the control has already said which filter
     * this is, and repeating it there reads as a stutter.
     */
    messageFor(parameter: string): string {
        return this.byParameter.get(parameter) || '';
    }

    /** Whether one filter has anything against it. */
    has(parameter: string): boolean {
        return this.byParameter.has(parameter);
    }
}

/**
 * What `detail` holds when the refusal named more than one thing.
 *
 * Exported so a caller that shows `detail` for its own reasons can recognise the
 * placeholder rather than printing it.
 */
export const MULTIPLE_ERRORS = 'Multiple errors occurred.';

/** `?name: message` split once, or undefined when the entry names no parameter. */
function parse(line: string): { parameter: string; message: string } | undefined {
    if (!line.startsWith('?')) {
        return undefined;
    }
    const separator = line.indexOf(': ');
    if (separator < 2) {
        return undefined;
    }
    const parameter = line.slice(1, separator).trim();
    const message = line.slice(separator + 2).trim();
    if (isEmpty(parameter) || isEmpty(message)) {
        return undefined;
    }
    return { parameter, message };
}
